import {
  ArcRotateCamera,
  Color3,
  Color4,
  Engine,
  HemisphericLight,
  Mesh,
  MeshBuilder,
  Scene,
  StandardMaterial,
  TransformNode,
  Vector3,
} from "@babylonjs/core";
import { detectNewestCrossing, erodeWeakTrail, shouldRecordPoint, type TrailPoint } from "./pathMemory";
import { loadSave, persistSave, type SaveData } from "./save";

type GameUi = {
  night: HTMLElement;
  points: HTMLElement;
  knots: HTMLElement;
  scuffed: HTMLElement;
  prompt: HTMLElement;
};

type TrailDot = {
  mesh: Mesh;
  point: TrailPoint;
};

function material(scene: Scene, name: string, color: Color3, emissive?: Color3, alpha = 1): StandardMaterial {
  const value = new StandardMaterial(name, scene);
  value.diffuseColor = color;
  value.alpha = alpha;
  if (emissive) value.emissiveColor = emissive;
  return value;
}

function createCat(scene: Scene): TransformNode {
  const cat = new TransformNode("cat", scene);
  const fur = material(scene, "cat-fur", new Color3(0.18, 0.17, 0.2));

  const body = MeshBuilder.CreateSphere("cat-body", { diameter: 1 }, scene);
  body.scaling.set(1.05, 0.55, 0.55);
  body.position.y = 0.48;
  body.material = fur;
  body.parent = cat;

  const head = MeshBuilder.CreateSphere("cat-head", { diameter: 0.58 }, scene);
  head.position.set(0, 0.58, 0.55);
  head.material = fur;
  head.parent = cat;

  for (const x of [-0.18, 0.18]) {
    const ear = MeshBuilder.CreateCylinder(
      "cat-ear",
      { height: 0.34, diameterTop: 0, diameterBottom: 0.24, tessellation: 3 },
      scene,
    );
    ear.position.set(x, 0.92, 0.56);
    ear.material = fur;
    ear.parent = cat;
  }

  const tail = MeshBuilder.CreateCylinder("cat-tail", { height: 1.15, diameter: 0.12, tessellation: 10 }, scene);
  tail.rotation.z = Math.PI / 2.7;
  tail.position.set(0, 0.58, -0.72);
  tail.material = fur;
  tail.parent = cat;

  return cat;
}

function createHouse(scene: Scene): void {
  const floorMaterial = material(scene, "floor", new Color3(0.19, 0.18, 0.2));
  const wallMaterial = material(scene, "walls", new Color3(0.32, 0.29, 0.32));

  const floor = MeshBuilder.CreateGround("floor", { width: 12, height: 8 }, scene);
  floor.material = floorMaterial;

  const walls = [
    { x: 0, z: -4, width: 12, depth: 0.18 },
    { x: 0, z: 4, width: 12, depth: 0.18 },
    { x: -6, z: 0, width: 0.18, depth: 8 },
    { x: 6, z: 0, width: 0.18, depth: 8 },
  ];

  for (const wall of walls) {
    const mesh = MeshBuilder.CreateBox("wall", { width: wall.width, height: 2.4, depth: wall.depth }, scene);
    mesh.position.set(wall.x, 1.2, wall.z);
    mesh.material = wallMaterial;
  }

  const sofa = MeshBuilder.CreateBox("sofa", { width: 3.1, height: 0.75, depth: 1.05 }, scene);
  sofa.position.set(-3.4, 0.38, -2.5);
  sofa.material = material(scene, "sofa-material", new Color3(0.26, 0.31, 0.34));

  const table = MeshBuilder.CreateBox("table", { width: 1.8, height: 0.1, depth: 1.1 }, scene);
  table.position.set(1.2, 0.75, -1.6);
  table.material = material(scene, "table-material", new Color3(0.31, 0.23, 0.17));

  const box = MeshBuilder.CreateBox("cardboard-box", { size: 1.1 }, scene);
  box.position.set(-4.5, 0.55, 2.3);
  box.material = material(scene, "box-material", new Color3(0.52, 0.38, 0.2));
}

function createTrailDot(scene: Scene, point: TrailPoint, ghost = false): Mesh {
  const dot = MeshBuilder.CreateSphere("path-memory", { diameter: ghost ? 0.055 : 0.075, segments: 6 }, scene);
  dot.position.set(point.x, 0.055, point.z);
  dot.material = material(
    scene,
    ghost ? "old-memory" : "new-memory",
    ghost ? new Color3(0.2, 0.35, 0.42) : new Color3(0.75, 0.45, 0.32),
    ghost ? new Color3(0.05, 0.12, 0.16) : new Color3(0.22, 0.08, 0.04),
    ghost ? 0.34 : 0.86,
  );
  return dot;
}

export async function createGame(canvas: HTMLCanvasElement, ui: GameUi): Promise<() => void> {
  const save = await loadSave();
  const engine = new Engine(canvas, true, { preserveDrawingBuffer: true, stencil: true });
  const scene = new Scene(engine);
  scene.clearColor = new Color4(0.055, 0.06, 0.085, 1);

  const ambient = new HemisphericLight("moonlight", new Vector3(0.2, 1, -0.3), scene);
  ambient.intensity = 0.72;
  ambient.diffuse = new Color3(0.54, 0.62, 0.78);

  createHouse(scene);
  const cat = createCat(scene);
  cat.position.set(-1.5, 0, 1.3);

  const camera = new ArcRotateCamera("camera", -Math.PI / 2.2, 1.08, 8.8, cat.position.clone(), scene);
  camera.lowerRadiusLimit = 6;
  camera.upperRadiusLimit = 11;
  camera.attachControl(canvas, true);

  const bed = MeshBuilder.CreateBox("cat-bed", { width: 1.55, height: 0.18, depth: 1.15 }, scene);
  bed.position.set(4.55, 0.1, 2.45);
  bed.material = material(scene, "bed-material", new Color3(0.41, 0.18, 0.24), new Color3(0.06, 0.015, 0.02));

  const vacuum = MeshBuilder.CreateCylinder("robot-vacuum", { diameter: 0.82, height: 0.17, tessellation: 30 }, scene);
  vacuum.position.set(0, 0.1, -2.8);
  vacuum.material = material(scene, "vacuum-material", new Color3(0.08, 0.09, 0.1));

  for (const oldNight of save.nights.slice(-6)) {
    for (let index = 0; index < oldNight.path.length; index += 4) {
      createTrailDot(scene, oldNight.path[index], true);
    }
  }

  let trail: TrailPoint[] = [{ x: cat.position.x, z: cat.position.z, t: performance.now() }];
  let dots: TrailDot[] = [{ point: trail[0], mesh: createTrailDot(scene, trail[0]) }];
  let knotCount = 0;
  let scuffedCount = 0;
  let lastVacuumPass = 0;
  const keys = new Set<string>();

  const updateUi = () => {
    ui.night.textContent = String(save.night);
    ui.points.textContent = String(trail.length);
    ui.knots.textContent = String(knotCount);
    ui.scuffed.textContent = String(scuffedCount);
    const closeToBed = Vector3.Distance(cat.position, bed.position) < 1.45;
    ui.prompt.textContent = closeToBed ? "Press E to curl up and end the night" : "Find the bed when you are ready to sleep";
  };

  const redrawTrail = () => {
    for (const dot of dots) dot.mesh.dispose();
    dots = trail.map((point) => ({ point, mesh: createTrailDot(scene, point) }));
  };

  const makeKnot = (x: number, z: number) => {
    const knot = MeshBuilder.CreateSphere("knot", { diameter: 0.28, segments: 8 }, scene);
    knot.position.set(x, 0.15, z);
    knot.material = material(
      scene,
      "knot-material",
      new Color3(0.72, 0.52, 0.24),
      new Color3(0.42, 0.16, 0.04),
    );
  };

  const endNight = async () => {
    if (Vector3.Distance(cat.position, bed.position) >= 1.45) return;
    save.nights.push({
      path: trail,
      knots: knotCount,
      endedAt: new Date().toISOString(),
    });
    if (save.nights.length > 24) save.nights.splice(0, save.nights.length - 24);
    save.night += 1;
    await persistSave(save);

    for (let index = 0; index < trail.length; index += 4) createTrailDot(scene, trail[index], true);
    for (const dot of dots) dot.mesh.dispose();

    cat.position.set(-1.5, 0, 1.3);
    trail = [{ x: cat.position.x, z: cat.position.z, t: performance.now() }];
    dots = [{ point: trail[0], mesh: createTrailDot(scene, trail[0]) }];
    knotCount = 0;
    scuffedCount = 0;
    updateUi();
  };

  const keyDown = (event: KeyboardEvent) => {
    keys.add(event.key.toLowerCase());
    if (event.key.toLowerCase() === "e") void endNight();
  };
  const keyUp = (event: KeyboardEvent) => keys.delete(event.key.toLowerCase());
  window.addEventListener("keydown", keyDown);
  window.addEventListener("keyup", keyUp);

  scene.onBeforeRenderObservable.add(() => {
    const dt = Math.min(engine.getDeltaTime() / 1000, 0.05);
    const movement = new Vector3(
      (keys.has("d") ? 1 : 0) - (keys.has("a") ? 1 : 0),
      0,
      (keys.has("s") ? 1 : 0) - (keys.has("w") ? 1 : 0),
    );

    if (movement.lengthSquared() > 0) {
      movement.normalize();
      cat.position.addInPlace(movement.scale(dt * 2.4));
      cat.position.x = Math.max(-5.45, Math.min(5.45, cat.position.x));
      cat.position.z = Math.max(-3.45, Math.min(3.45, cat.position.z));
      cat.rotation.y = Math.atan2(movement.x, movement.z);
      camera.target = Vector3.Lerp(camera.target, cat.position, 0.1);

      const point = { x: cat.position.x, z: cat.position.z, t: performance.now() };
      if (shouldRecordPoint(trail, point)) {
        trail.push(point);
        dots.push({ point, mesh: createTrailDot(scene, point) });
        const crossing = detectNewestCrossing(trail);
        if (crossing) {
          knotCount += 1;
          makeKnot(crossing.x, crossing.z);
        }
      }
    }

    const now = performance.now();
    vacuum.position.x = Math.sin(now * 0.00042) * 4.35;
    vacuum.position.z = -2.7 + Math.sin(now * 0.00019) * 0.58;

    if (now - lastVacuumPass > 650) {
      const eroded = erodeWeakTrail(trail, { x: vacuum.position.x, z: vacuum.position.z }, 0.52);
      if (eroded.removed > 0) {
        trail = eroded.points;
        scuffedCount += eroded.removed;
        redrawTrail();
      }
      lastVacuumPass = now;
    }

    updateUi();
  });

  engine.runRenderLoop(() => scene.render());
  const resize = () => engine.resize();
  window.addEventListener("resize", resize);
  updateUi();

  return () => {
    window.removeEventListener("keydown", keyDown);
    window.removeEventListener("keyup", keyUp);
    window.removeEventListener("resize", resize);
    scene.dispose();
    engine.dispose();
  };
}
